import { SelectOption } from '@fly/core';
import { apiSlice } from '@fly/store';
import { createSelector } from '@reduxjs/toolkit';

import {
  CheckoutStatusRawResponse,
  CheckoutStatusResponse,
  PaymentCountryResponse,
  PaymentPlansResponse,
  PrepareCheckoutRequest,
  PrepareCheckoutResponse,
  SubmitCheckoutRequest,
  SubmitCheckoutResponse,
} from '../types';

export const paymentService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCountries: builder.query<PaymentCountryResponse[], void>({
      query: () => ({
        api: 'billing_reference',
        url: 'countries',
        method: 'GET',
      }),
      providesTags: ['payment'],
    }),
    getPlans: builder.query<PaymentPlansResponse[], void>({
      query: () => ({
        api: 'billing_reference',
        url: 'pricing',
        method: 'GET',
      }),
      transformResponse: (response: { plans: PaymentPlansResponse[] }) =>
        response.plans,
      providesTags: ['payment'],
    }),
    prepareCheckout: builder.mutation<
      PrepareCheckoutResponse,
      { subscriptionId: string; body: PrepareCheckoutRequest }
    >({
      query: ({ subscriptionId, body }) => ({
        api: 'auth',
        url: `checkout/subscriptions/${subscriptionId}/prepare`,
        method: 'POST',
        data: {
          ...body,
        },
      }),
    }),
    submitCheckout: builder.mutation<
      SubmitCheckoutResponse,
      { subscriptionId: string; body: SubmitCheckoutRequest }
    >({
      query: ({ subscriptionId, body }) => ({
        api: 'auth',
        url: `checkout/subscriptions/${subscriptionId}`,
        method: 'POST',
        data: body,
      }),
    }),
    getCheckoutStatus: builder.query<
      CheckoutStatusResponse,
      { subscriptionId: string }
    >({
      query: ({ subscriptionId }) => ({
        api: 'auth',
        url: `checkout/subscriptions/${subscriptionId}/status`,
        method: 'GET',
      }),
      transformResponse: (
        response: CheckoutStatusRawResponse
      ): CheckoutStatusResponse => {
        const order = response.orders?.[0];
        if (!order) {
          return { status: 'pending', message: 'No order status available' };
        }
        return {
          status: order.status,
          message:
            order.status === 'error'
              ? 'Purchase failed'
              : order.status === 'success'
                ? 'Purchase completed'
                : 'Processing purchase',
          error_details: order.error_details,
        };
      },
    }),
  }),
});

export const {
  useGetCountriesQuery,
  useGetPlansQuery,
  usePrepareCheckoutMutation,
  useSubmitCheckoutMutation,
  useLazyGetCheckoutStatusQuery,
  useGetCheckoutStatusQuery,
} = paymentService;

export const selectPaymentPlansResult =
  paymentService.endpoints.getPlans.select() || [];

export const selectCountriesResult =
  paymentService.endpoints.getCountries.select() || [];

export const selectCountriesKeyValue = createSelector(
  selectCountriesResult,
  (result) =>
    !result?.data
      ? []
      : result?.data?.map(
          ({ name }): SelectOption => ({
            value: name,
            textValue: name,
          })
        ) || []
);

export const selectPaymentKeyValue = createSelector(
  selectPaymentPlansResult,
  (result) =>
    !result?.data
      ? []
      : result?.data?.map(
          ({ id, name }): SelectOption => ({
            value: id,
            textValue: name,
          })
        ) || []
);
